import { useQuery } from "@tanstack/react-query";
import { fetcher } from "../../../utils/Axios";
import { getAuthValue } from "../../authModule/hooks/useAuthValue";
import { USER, USER_STATUS } from "../../userModule/types/CommonTypes";
import { COPILOT_RECIPIENT } from "../types/CommonTypes";

const BROADCAST_RECIPIENTS_QUERY_KEY = "copilot/broadcast-recipients";

type ROLE_DEPT_MAPPING = {
  id: number;
  role: string;
  department: string;
};

export type ELIGIBLE_USER_FOR_SMS = {
  id: string;
  role: string;
  phoneNumber: string;
};

export type PERSONNEL_FOR_PROCESS_MESSAGE = {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
  department: string;
  phoneNumber: string;
};

export type BROADCAST_DATA = {
  recipientOptions: {
    users: COPILOT_RECIPIENT[];
    roles: COPILOT_RECIPIENT[];
    departments: COPILOT_RECIPIENT[];
  };
  eligibleUsers: ELIGIBLE_USER_FOR_SMS[];
  roleToDepts: Record<string, string[]>;
  personnelDatabase: PERSONNEL_FOR_PROCESS_MESSAGE[];
};

async function getBroadcastData(
  tenantId: string,
  hospitalId: string
): Promise<BROADCAST_DATA> {
  const headers = { "x-tenant-id": tenantId, "x-hospital-id": hospitalId };
  const [usersRes, rolesRes, mappingRes] = await Promise.all([
    fetcher({ url: `/users/all?status=${USER_STATUS.ACTIVE}`, headers }),
    fetcher({ url: "users/roles", headers }),
    fetcher({ url: "role-department-mapping", headers }),
  ]);

  const allUsers: USER[] = usersRes.data ?? [];
  const roles: string[] = rolesRes.data?.roles ?? [];
  const mapping: ROLE_DEPT_MAPPING[] = mappingRes.data ?? [];

  const eligibleUsers = allUsers.filter(
    (u) =>
      u.notificationConfig?.isEligibleForAlerts === true &&
      (u.notificationConfig.countryCode?.trim() ?? "").length > 0 &&
      (u.notificationConfig.phoneNumber?.trim() ?? "").length > 0
  );

  // role -> departments
  const roleToDepts: Record<string, string[]> = {};
  mapping.forEach(({ role, department }) => {
    if (!roleToDepts[role]) roleToDepts[role] = [];
    if (!roleToDepts[role].includes(department)) roleToDepts[role].push(department);
  });

  // eligible user count per role
  const roleCount: Record<string, number> = {};
  eligibleUsers.forEach((u) => {
    if (u.role) roleCount[u.role] = (roleCount[u.role] ?? 0) + 1;
  });

  // eligible user count per department (via role->dept mapping)
  const deptCount: Record<string, number> = {};
  eligibleUsers.forEach((u) => {
    (roleToDepts[u.role] ?? []).forEach((dept) => {
      deptCount[dept] = (deptCount[dept] ?? 0) + 1;
    });
  });

  const users: COPILOT_RECIPIENT[] = eligibleUsers.map((u) => ({
    id: String(u.id),
    label: u.role ? `${u.firstName} ${u.lastName} (${u.role})` : `${u.firstName} ${u.lastName}`,
    count: 1,
    type: "user" as const,
  }));

  const rolesOptions: COPILOT_RECIPIENT[] = roles
    .filter((r) => (roleCount[r] ?? 0) > 0)
    .map((r) => ({ id: r, label: r, count: roleCount[r], type: "role" as const }));

  const departments: COPILOT_RECIPIENT[] = Object.entries(deptCount)
    .filter(([, count]) => count > 0)
    .map(([dept, count]) => ({ id: dept, label: dept, count, type: "department" as const }));

  const eligibleUsersForSms: ELIGIBLE_USER_FOR_SMS[] = eligibleUsers.map((u) => ({
    id: String(u.id),
    role: u.role,
    phoneNumber:
      (u.notificationConfig.countryCode?.trim() ?? "") +
      (u.notificationConfig.phoneNumber?.trim() ?? ""),
  }));

  const personnelDatabase: PERSONNEL_FOR_PROCESS_MESSAGE[] = eligibleUsers.map((u) => ({
    id: Number(u.id),
    first_name: u.firstName,
    last_name: u.lastName,
    role: u.role,
    department: (roleToDepts[u.role] ?? []).join(", "),
    phoneNumber:
      (u.notificationConfig.countryCode?.trim() ?? "") +
      (u.notificationConfig.phoneNumber?.trim() ?? ""),
  }));

  return {
    recipientOptions: { users, roles: rolesOptions, departments },
    eligibleUsers: eligibleUsersForSms,
    roleToDepts,
    personnelDatabase,
  };
}

export default function useGetBroadcastRecipientsQuery() {
  const { tenantId, hospitalId } = getAuthValue();
  return useQuery({
    queryKey: [BROADCAST_RECIPIENTS_QUERY_KEY, tenantId, hospitalId],
    queryFn: () => getBroadcastData(tenantId ?? "", hospitalId ?? ""),
    enabled: !!tenantId && !!hospitalId,
  });
}
